import React, { useState, useEffect } from "react";
import { withStyles } from "@material-ui/core/styles";
import { withTranslation } from "react-i18next";
import ApiTour from "../../../../../API/ApiTour";
import styles from "./TemplateTourDialogStyles";
import TemplateTourDialogView from "./TemplateTourDialogView";

const TemplateTourDialogComponent = ({ open, onClose, onCopySuccess, onSuccessAlert, classes, t }) => {
  const [selectedThemeIndex, setSelectedThemeIndex] = useState(0);
  const [templateData, setTemplateData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [copyingTourId, setCopyingTourId] = useState(null);
  const [descriptionAnchorEl, setDescriptionAnchorEl] = useState(null);
  const [selectedTourForDescription, setSelectedTourForDescription] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [requestDialogTour, setRequestDialogTour] = useState(null);
  const [isRequesting, setIsRequesting] = useState(false);
  const [requestSuccess, setRequestSuccess] = useState(false);

  useEffect(() => {
    if (open) {
      fetchTemplates();
    }
  }, [open]);

  const ON_REQUEST_OVERRIDES = {
    594: {
      type: "on_request",
      nbEnigmas: 151,
      nbZones: 1,
      moreInfoUrl: "https://landingzone.app/world-flags-activite-team-building-grand-groupe/",
    },
  };

  const applyOverrides = (themes) =>
    (themes || []).map((theme) => ({
      ...theme,
      tours: (theme.tours || []).map((tour) =>
        ON_REQUEST_OVERRIDES[tour.id]
          ? { ...tour, ...ON_REQUEST_OVERRIDES[tour.id] }
          : tour
      ),
    }));

  const fetchTemplates = async () => {
    setIsLoading(true);
    setErrorMessage("");
    const response = await ApiTour.getTemplateTours();
    if (response.status === 200) {
      setTemplateData(applyOverrides(response.data.themes));
      setSelectedThemeIndex(0);
    } else {
      setErrorMessage(t("template_tour_dialog.error_loading"));
    }
    setIsLoading(false);
  };

  const handleCopyTour = async (tourId) => {
    setCopyingTourId(tourId);
    setErrorMessage("");
    const response = await ApiTour.copyTemplateTour(tourId);
    if (response.status === 201) {
      onCopySuccess();
      handleClose();
      if (onSuccessAlert) {
        onSuccessAlert(t("template_tour_dialog.success_copy"));
      }
    } else if (response.status === 403) {
      setErrorMessage(t("template_tour_dialog.error_limit_reached"));
      setCopyingTourId(null);
    } else {
      setErrorMessage(t("template_tour_dialog.error_copy"));
      setCopyingTourId(null);
    }
  };

  const handleOpenDescription = (event, tour) => {
    setDescriptionAnchorEl(event.currentTarget);
    setSelectedTourForDescription(tour);
  };

  const handleCloseDescription = () => {
    setDescriptionAnchorEl(null);
    setSelectedTourForDescription(null);
  };

  const handleOpenRequestDialog = (tour) => {
    setRequestDialogTour(tour);
    setRequestSuccess(false);
  };

  const handleCloseRequestDialog = () => {
    setRequestDialogTour(null);
    setIsRequesting(false);
    setRequestSuccess(false);
  };

  const handleConfirmRequest = async () => {
    if (!requestDialogTour) return;
    setIsRequesting(true);
    const response = await ApiTour.requestTemplateTour(requestDialogTour.id);
    setIsRequesting(false);
    if (response.status === 200 || response.status === 201) {
      setRequestSuccess(true);
    } else {
      setErrorMessage(t("template_tour_dialog.error_request"));
      handleCloseRequestDialog();
    }
  };

  const handleClose = () => {
    setErrorMessage("");
    onClose();
  };

  const currentTours =
    selectedThemeIndex === 0
      ? templateData.flatMap((theme) =>
        (theme.tours || []).map((tour) => ({ ...tour, themeName: theme.name }))
      )
      : (templateData[selectedThemeIndex - 1]?.tours || []).map((tour) => ({
        ...tour,
        themeName: templateData[selectedThemeIndex - 1]?.name,
      }));

  return (
    <TemplateTourDialogView
      open={open}
      onClose={handleClose}
      classes={classes}
      t={t}
      selectedThemeIndex={selectedThemeIndex}
      setSelectedThemeIndex={setSelectedThemeIndex}
      templateData={templateData}
      currentTours={currentTours}
      isLoading={isLoading}
      copyingTourId={copyingTourId}
      handleCopyTour={handleCopyTour}
      descriptionAnchorEl={descriptionAnchorEl}
      selectedTourForDescription={selectedTourForDescription}
      handleOpenDescription={handleOpenDescription}
      handleCloseDescription={handleCloseDescription}
      errorMessage={errorMessage}
      requestDialogTour={requestDialogTour}
      isRequesting={isRequesting}
      requestSuccess={requestSuccess}
      handleOpenRequestDialog={handleOpenRequestDialog}
      handleCloseRequestDialog={handleCloseRequestDialog}
      handleConfirmRequest={handleConfirmRequest}
    />
  );
};

export default withStyles(styles, { withTheme: true })(
  withTranslation()(TemplateTourDialogComponent)
);
